import {
  GridCellProps,
  GridColumn as Column,
} from '@progress/kendo-react-grid';
import {
  DateCell,
  EditDeleteScheduleCell,
  FilterBarMainGrid,
  LinkCell,
} from 'Components/Grid';
import {
  FormProvider,
  GridProvider,
  useEnums,
  useGlobalContext,
} from 'Context';
import {
  defaultGridProps,
  FilterBarFilterType,
  FilterConfig,
  IFilterConfig,
  StaticFilterConfig,
} from 'Models';
import { Permission } from 'Models/Templates/Permission/Permission';
import { ENDPOINTS } from 'Models/Templates/Routes/Routes';
import {
  addEditRoute,
  AddEditSubRoute,
  ClaimantsSubRoute,
  ClaimsSubRoute,
  ReferralSubRoute,
  ROUTES,
} from 'Navigation';
import React, { Fragment, HTMLProps, useEffect, useState } from 'react';
import {
  DEFAULT_DATETIME24H_FORMAT,
  EMPTY_GUID,
  ExpeditedPriority,
  buildEndpoint,
  buildRoute,
  useDataFetcher,
  useGridSearch,
} from 'Utilities';
import Warning from '@mui/icons-material/Warning';
import { Tooltip } from '@mui/material';
import { DefaultColumnsSelection } from 'Models/ColumnSelection';
import {
  ColumnSelectorProvider,
  useColumnSelectorState,
} from 'Context/ColumnSelectorProvider';
import NewReferralButton from 'v3/Components/Shared/NewReferralButton';
import { DebouncedTextFilterCell } from 'Components/Grid/FilterCells/DebouncedTextFilterCell';
import { DropdownDbOption } from 'v3/Components/Form/Dropdown';
import { DatePickerFilterCell } from 'Components/Grid/FilterCells/DatePickerFilterCell';
import { DropdownFilterCell } from 'Components/Grid/FilterCells/DropdownFilterCell';
import { appointmentStatusCell } from 'v3/Components/Shared/StatusPill';
import { useHistory } from 'react-router-dom';
import { dueDateTimeCell } from 'Components/Grid/DisplayCells/CustomCells';
import { ColumnMenu } from 'Components/Grid/TableHeader/ColumnMenu';
import { DateRangePickerFilterCell } from 'Components/Grid/FilterCells/DateRangePickerFilterCell';

export const Appointments: React.FC = () => {
  return (
    <Fragment>
      <div className="main-grid-header">
        <h1>Assigning Queue</h1>
        <NewReferralButton />
      </div>
      <AppointmentsFilteredGrid id={EMPTY_GUID} />
    </Fragment>
  );
};

class AppointmentSearch {
  searchKey?: string;
  referralId?: string;
  client?: string;
  benefitState?: string;
}

export enum AppointmentsGridIds {
  DetailedSearch = 'detailedSearch',
}

export type AppointmentsFilteredGridProps =
  HTMLProps<AppointmentsFilteredGridProps> & {
    id?: string;
  };

export const AppointmentsFilteredGrid: React.FC<
  AppointmentsFilteredGridProps
> = ({ id, ...props }) => {
  const appointmentSearch = new AppointmentSearch();
  appointmentSearch.referralId = id;
  return (
    <FormProvider formModel={appointmentSearch}>
      <ColumnSelectorProvider
        columns={DefaultColumnsSelection}
        columnSelectorName="appointmentColumns"
      >
        <GridProvider entityName="Appointment">
          <AppointmentsGrid id={id} />
        </GridProvider>
      </ColumnSelectorProvider>
    </FormProvider>
  );
};

type FilterModel = {
  services?: DropdownDbOption[];
  submittedBy?: DropdownDbOption[];
  subServices?: DropdownDbOption[];
};
export const AppointmentsGrid: React.FC<AppointmentsFilteredGridProps> = ({
  id,
  ...props
}) => {
  const { state } = useEnums();
  const columnSelectorState = useColumnSelectorState();
  const globalContext = useGlobalContext();
  const userdata = globalContext[0]?.currentUser;
  const showDateFilters = userdata?.isInternal || userdata?.isAdmin;
  const [filtersModel, filtersStatus] = useDataFetcher<FilterModel>(
    `${ENDPOINTS.opencases.base}/GetFiltersModel`,
    {},
    (d) => d.data,
    undefined,
    true,
    '3.0'
  );

  const [exportEndpoint, setExportEndpoint] = useState(
    buildEndpoint(
      ENDPOINTS.appointments.endpointString('ExportToCsv') +
        `${getSelectedColumnsQuery(columnSelectorState)}`
    )
  );

  useEffect(() => {
    setExportEndpoint(
      buildEndpoint(
        ENDPOINTS.appointments.endpointString('ExportToCsv') +
          `${getSelectedColumnsQuery(columnSelectorState)}`
      )
    );
  }, [columnSelectorState]);

  const filters: IFilterConfig[] = [
    new FilterConfig(
      'searchKey',
      FilterBarFilterType.SearchText,
      'Stradix Number, Claim Number, Claimant, Referral Number, Doctor'
    ),
    new FilterConfig(
      'clients',
      FilterBarFilterType.AsyncDropdown,
      'Client',
      {
        readEndpoint: ENDPOINTS.clients.dropdownItems,
      },
      (value) => value.name,
      true
    ),
    new FilterConfig(
      'benefitStates',
      FilterBarFilterType.EnumDropdown,
      'State of Jurisdiction',
      { data: state },
      (value) => value.displayName,
      true
    ),
    new FilterConfig(
      'appointmentDate',
      FilterBarFilterType.DatePicker,
      'Appointment Date'
    ),
    new FilterConfig(
      'referralDateFrom',
      FilterBarFilterType.DatePicker,
      'Referral Date From'
    ),
    new FilterConfig(
      'referralDateTo',
      FilterBarFilterType.DatePicker,
      'Referral Date To'
    ),
    new StaticFilterConfig('referralId'),
  ];
  const [gridToggler] = useGridSearch();
  const [filtersEnabled, setFiltersEnabled] = useState(true);
  const dateColumnWidth = filtersEnabled ? '260px' : '200px';

  let endpoint = ENDPOINTS.appointments.read;

  const history = useHistory();

  function navigateToReferral(dataItem: any) {
    history.push(
      buildRoute(AddEditSubRoute.Referral, dataItem.referralId, ROUTES.VIEW)
    );
  }

  return (
    <React.Fragment>
      <FilterBarMainGrid
        gridId="AssigningReferrals"
        style={{ cursor: 'pointer' }}
        onRowClick={(e) => navigateToReferral(e.dataItem)}
        isMainGrid
        headerTitle="Referrals"
        filterBarProps={{
          filterConfigurations: [],
          filterValues: {},
          isColumnSelectorEnabled: false,
        }}
        refreshToggle={gridToggler}
        readEndpoint={endpoint}
        apiVersion="3.0"
        exportEndpoint={exportEndpoint}
        enableGridRefresh={true}
        defaultFiltersEnabled={true}
        onFiltersEnabledChange={setFiltersEnabled}
        {...defaultGridProps}
      >
        <Column
          field="rejected"
          title={' '}
          width="50"
          cell={RejectionCell}
          filterable={false}
          sortable={false}
        />

        {columnSelectorState.columns.find((x) => x.field === 'stradixNumber')
          ?.checked && (
          <Column
            field="stradixNumber"
            cell={ReferralNumberCell}
            title="Stradix Number"
            width="150px"
            filterable
            filterCell={DebouncedTextFilterCell}
            columnMenu={ColumnMenu}
          />
        )}
        {columnSelectorState.columns.find((x) => x.field === 'claimantName')
          ?.checked && (
          <Column
            field="claimantName"
            title="Claimant Name"
            cell={ClaimantCell}
            width="200px"
            filterable
            filterCell={DebouncedTextFilterCell}
            columnMenu={ColumnMenu}
          />
        )}
        {columnSelectorState.columns.find((x) => x.field === 'clientName')
          ?.checked && (
          <Column
            field="clientName"
            title="Client"
            width="200px"
            filterable
            filterCell={DebouncedTextFilterCell}
            columnMenu={ColumnMenu}
          />
        )}
        {columnSelectorState.columns.find((x) => x.field === 'claimNumber')
          ?.checked && (
          <Column
            field="claimNumber"
            title="Claim Number"
            cell={ClaimCell}
            width="200px"
            filterable
            filterCell={DebouncedTextFilterCell}
            columnMenu={ColumnMenu}
          />
        )}
        {columnSelectorState.columns.find((x) => x.field === 'referralDate')
          ?.checked && (
          <Column
            field="referralDate"
            title="Referral Date"
            cell={dateFormatCell}
            width={dateColumnWidth}
            filterable
            filterCell={(e) => <DateRangePickerFilterCell {...e} />}
            columnMenu={ColumnMenu}
          />
        )}
        {columnSelectorState.columns.find((x) => x.field === 'reportDueDate')
          ?.checked && (
          <Column
            field="reportDueDate"
            title="Report Due Date"
            cell={dateFormatCell}
            width={dateColumnWidth}
            filterable
            filterCell={(e) => <DateRangePickerFilterCell {...e} />}
            columnMenu={ColumnMenu}
          />
        )}
        {columnSelectorState.columns.find((x) => x.field === 'referralDueDate')
          ?.checked && (
          <Column
            field="referralDueDate"
            title="Client Due Date"
            cell={dueDateTimeCell}
            width={dateColumnWidth}
            filterable
            filterCell={(e) => <DateRangePickerFilterCell {...e} />}
            columnMenu={ColumnMenu}
          />
        )}
        {columnSelectorState.columns.find((x) => x.field === 'benefitState')
          ?.checked && (
          <Column
            field="benefitState"
            title="State of Jurisdiction"
            width="200px"
            filterCell={(x) => DropdownFilterCell(x, state)}
            columnMenu={ColumnMenu}
          />
        )}
        {columnSelectorState.columns.find((x) => x.field === 'status')
          ?.checked && (
          <Column
            field="appointmentStatusName"
            title="Status"
            width="200px"
            filterable={false}
            columnMenu={ColumnMenu}
            cell={(e) =>
              appointmentStatusCell({
                ...e,
                onClick: () => navigateToReferral(e.dataItem),
              })
            }
          />
        )}
        {columnSelectorState.columns.find((x) => x.field === 'typeOfEval')
          ?.checked && (
          <Column
            field="typeOfEval"
            title="Type of EVAL"
            width="200px"
            filterable
            filterCell={DebouncedTextFilterCell}
            columnMenu={ColumnMenu}
          />
        )}
        {columnSelectorState.columns.find((x) => x.field === 'appointmentDate')
          ?.checked && (
          <Column
            field="appointmentDate"
            title="Appointment Date"
            cell={dateFormatCell}
            width={dateColumnWidth}
            filterable
            filterCell={(e) => <DateRangePickerFilterCell {...e} />}
          />
        )}
        {columnSelectorState.columns.find(
          (x) => x.field === 'treatingPhysician'
        )?.checked && (
          <Column
            field="treatingPhysician"
            title="Treating Physician"
            width="200px"
            filterable
            filterCell={DebouncedTextFilterCell}
          />
        )}
        {columnSelectorState.columns.find(
          (x) => x.field === 'physicianSpecialty'
        )?.checked && (
          <Column
            field="physicianSpecialty"
            title="Physician Specialty"
            width="200px"
            filterable
            filterCell={DebouncedTextFilterCell}
          />
        )}
        {columnSelectorState.columns.find((x) => x.field === 'insuranceCarrier')
          ?.checked && (
          <Column
            field="insuranceCarrier"
            title="Insurance Carrier"
            width="200px"
            filterable
            filterCell={DebouncedTextFilterCell}
          />
        )}
        {columnSelectorState.columns.find((x) => x.field === 'referralSource')
          ?.checked && (
          <Column
            field="referralSource"
            title="Referral Source"
            width="200px"
            filterCell={(x) =>
              DropdownFilterCell(
                x,
                undefined,
                filtersStatus === 'success' ? filtersModel.submittedBy : []
              )
            }
          />
        )}
        {columnSelectorState.columns.find((x) => x.field === 'adjusterName')
          ?.checked && (
          <Column
            field="adjusterName"
            title="Adjuster Name"
            width="200px"
            filterable
            filterCell={DebouncedTextFilterCell}
          />
        )}
        {columnSelectorState.columns.find((x) => x.field === 'doctor')
          ?.checked && (
          <Column
            field="professionalName"
            title="Doctor"
            width="200px"
            filterable
            filterCell={DebouncedTextFilterCell}
          />
        )}
        {columnSelectorState.columns.find((x) => x.field === 'closedDate')
          ?.checked && (
          <Column
            field="closedDate"
            title="Date Closed"
            cell={dateFormatCellRaw}
            width={dateColumnWidth}
            filterable
            filterCell={(e) => <DateRangePickerFilterCell {...e} dateOnly />}
          />
        )}
        {columnSelectorState.columns.find(
          (x) => x.field === 'checkMedicalsDate'
        )?.checked && (
          <Column
            field="checkMedicalsDate"
            title="Check Medicals Date"
            cell={dateFormatCellRaw}
            width={dateColumnWidth}
            filterable
            filterCell={(e) => <DateRangePickerFilterCell {...e} dateOnly />}
          />
        )}
        <Column
          field="ownerName"
          title="Owner"
          width="160px"
          filterCell={DebouncedTextFilterCell}
        />
        <Column
          field="id"
          width="50px"
          title="Actions"
          cell={ActionsCell}
          filterable={false}
          sortable={false}
          filterCell={DebouncedTextFilterCell}
        />
      </FilterBarMainGrid>
    </React.Fragment>
  );
};

const ReferralNumberCell: React.FC<GridCellProps> = (props: GridCellProps) => {
  return (
    <LinkCell
      endpoint={ROUTES.REFERRAL}
      subRoute={ReferralSubRoute.View}
      linkField="referralId"
      {...props}
    ></LinkCell>
  );
};

const ClaimantCell: React.FC<GridCellProps> = (props: GridCellProps) => {
  return (
    <LinkCell
      endpoint={ROUTES.CLAIMANT}
      subRoute={ClaimantsSubRoute.View}
      linkField="claimantId"
      {...props}
    ></LinkCell>
  );
};

const ClaimCell: React.FC<GridCellProps> = (props: GridCellProps) => {
  return (
    <LinkCell
      endpoint={ROUTES.CLAIM}
      subRoute={ClaimsSubRoute.View}
      linkField="claimId"
      {...props}
    ></LinkCell>
  );
};

const ActionsCell: React.FC<GridCellProps> = (props: GridCellProps) => {
  return (
    <EditDeleteScheduleCell
      editRoute={addEditRoute(AddEditSubRoute.Appointment)}
      editPermissions={[Permission.EditAppointment]}
      deleteEndpoint={ENDPOINTS.appointments.base}
      deletePermissions={[Permission.DeleteAppointment]}
      scheduleRoute={addEditRoute(AddEditSubRoute.Appointment)}
      schedulePermissions={[Permission.EditAppointment]}
      {...props}
    />
  );
};

const RejectionCell: React.FC<GridCellProps> = (props: GridCellProps) => {
  return (
    <td>
      <div className="flex-vertical-right flex-end">
        <span>
          {props.dataItem.rejected ? (
            <>
              <Tooltip
                title={`Rejected by ${props.dataItem.rejectedBy} - ${props.dataItem.rejectionReason}`}
                placement="right"
              >
                <Warning />
              </Tooltip>
            </>
          ) : null}
        </span>
      </div>
    </td>
  );
};

const dateFormatCellRaw: React.FC<GridCellProps> = (props: GridCellProps) => {
  return <DateCell {...props} dateFormat={'MM/DD/YYYY'} disableUtcConversion />;
};

const dateFormatCell: React.FC<GridCellProps> = (props: GridCellProps) => {
  return <DateCell {...props} dateFormat={'MM/DD/YYYY'} />;
};

const getSelectedColumnsQuery = (columnSelectorState: any) => {
  const selectedColumns = columnSelectorState.columns
    .filter((x: any) => x.checked)
    .map((x: any) => x.field);
  return '?' + selectedColumns.map((x: any) => `columns=${x}`).join('&');
};
